// ABOUTME: Calendar component showing heart achievements, supports week view and full month view.
// ABOUTME: Enhanced with month navigation, day selection, expand/collapse toggle, and timezone support.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Popover, Card, CardContent, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { FavoriteOutlined as HeartIcon, ArrowBack, ArrowForward, ExpandMore, ExpandLess, FlightTakeoff as AwayIcon } from '@mui/icons-material';
import { getCurrentWeekDateRange } from '../utils/calendarUtils';
import type { DailyProgress } from '../types/api';
import naluV1Theme from '../theme/theme.nalu-v1';

// Behavior rows for the day tooltip card
const BEHAVIOR_ROWS = [
  { key: 'starCount', label: 'Stars', emoji: '⭐' },
  { key: 'triangleCount', label: 'Triangles', emoji: '⚠️' },
  { key: 'squareCount', label: 'Storms', emoji: '🛑' },
  { key: 'makeItRightCount', label: 'Make It Right', emoji: '🤝' },
  { key: 'flipYourCoinCount', label: 'Reset', emoji: '🪙' },
  { key: 'oneOnOneTimeCount', label: 'Let Go', emoji: '🕐' },
] as const;

// Tooltip card shown when hovering a past calendar day
const DayTooltipCard: React.FC<{ dateStr: string | null; progress?: DailyProgress }> = ({ dateStr, progress }) => {
  if (!dateStr) return null;

  const hasAnyBehavior = progress && (
    progress.starCount > 0 || progress.triangleCount > 0 ||
    progress.squareCount > 0 || progress.makeItRightCount > 0 ||
    progress.flipYourCoinCount > 0 || progress.oneOnOneTimeCount > 0
  );

  if (!progress || !hasAnyBehavior) {
    return (
      <Card sx={{ p: 1.5, minWidth: 180 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Typography variant="body2" color="text.secondary">No behaviors logged</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 0, minWidth: 200 }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
          {dateStr}
        </Typography>
        {BEHAVIOR_ROWS.map(({ key, label, emoji }) => (
          <Box key={key} display="flex" justifyContent="space-between" gap={2} mb={0.25}>
            <Typography variant="body2">{emoji} {label}</Typography>
            <Typography variant="body2" fontWeight="medium">{((progress as unknown) as Record<string, number>)[key] ?? 0}</Typography>
          </Box>
        ))}
        {!progress.earnedHeart && progress.noHeartReasons && progress.noHeartReasons.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="error" fontWeight="bold" display="block" mb={0.5}>
              Why no heart:
            </Typography>
            {progress.noHeartReasons.map((reason, i) => (
              <Typography key={i} variant="caption" color="error" display="block">
                • {reason}
              </Typography>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Constants for calendar layout and styling
const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const CELL_HEIGHT = 48; // tall enough that the heart icon clears the day-number corner (issue #1900)
const DAY_NUMBER_FONT_SIZE = '0.65rem';
// 20px matches h4's fontSize; used for icon/emoji glyph sizing, not a Typography text override
const HEART_ICON_SIZE = 20;

interface HeartCalendarProps {
  heartDates: string[]; // ISO date strings — clean heart days (no square/MIR cycle)
  mendingHeartDates?: string[]; // ISO date strings — heart earned after a square+MakeItRight cycle
  loading?: boolean;
  // View mode: 'week' shows current 7-day week, 'month' shows full month with navigation
  viewMode?: 'week' | 'month';
  // When provided, shows expand/collapse toggle and persists view state to localStorage
  localStorageKey?: string;
  familyTimezone?: string;
  familyCreatedAt?: string; // ISO date string
  onDaySelect?: (date: Date) => void;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  selectedDate?: Date; // For controlled selection
  dailyProgress?: DailyProgress[]; // All daily progress records for the visible date range
  // When true, renders just the day grid — no surrounding Paper, header, or footer summary.
  // For embedding as one card among several (e.g. the All Kids dashboard tab, issue #1900).
  hideChrome?: boolean;
  // Overrides the chrome Paper's border color (e.g. a per-child color, issue #1900) — falls
  // back to the theme's default Paper border when not provided.
  borderColor?: string;
}

const HeartCalendar: React.FC<HeartCalendarProps> = ({
  heartDates = [],
  mendingHeartDates = [],
  loading = false,
  viewMode: propViewMode = 'month',
  localStorageKey,
  familyTimezone,
  familyCreatedAt,
  onDaySelect,
  onDateRangeChange,
  selectedDate: propSelectedDate,
  dailyProgress = [],
  hideChrome = false,
  borderColor,
}) => {
  const theme = useTheme();
  // nalu-v1's day cells use the mockup's 8px radius (issue #1900); other themes keep
  // the existing theme-relative radius. Compared by value, not identity — see
  // DashboardRedesigned's isNaluV1Theme for why (dev-server HMR reference breakage).
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  // Get current date (date-only, midnight) in family timezone or browser timezone.
  // Formats straight to a YYYY-MM-DD string and parses that into local-midnight
  // components — no round-trip through a bare `new Date(formattedString)`, which
  // would reinterpret the formatted wall-clock time in the browser's own timezone
  // and silently shift the day. Date-only (not time-of-day-bearing) so it stays
  // consistent with day cells, which are also date-only (noon-anchored) constructs.
  const getCurrentDateInTimezone = useCallback(() => {
    const timezone = familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: timezone });
    const [year, month, day] = todayStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [familyTimezone]);

  const today = getCurrentDateInTimezone();

  // View mode state — persisted in localStorage when localStorageKey is provided
  // The prop viewMode sets the default; localStorage only overrides if the user
  // has explicitly toggled within the same context (mobile week↔month).
  const [activeViewMode, setActiveViewMode] = useState<'week' | 'month'>(() => {
    if (localStorageKey) {
      const saved = localStorage.getItem(localStorageKey);
      if (saved === 'week' || saved === 'month') return saved;
    }
    return propViewMode;
  });

  // Sync with prop when the parent changes viewMode (e.g., desktop↔mobile resize)
  // Skip the first run so localStorage-initialized state is not overridden on mount
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    // Intentional: re-syncing local view-mode state from the prop after mount is the
    // point of this effect (desktop<->mobile resize should override the localStorage-
    // initialized value). Computing this during render isn't viable here since we need
    // to distinguish "first render" from "prop changed after mount".
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveViewMode(propViewMode);
  }, [propViewMode]);

  const handleToggleViewMode = () => {
    const next = activeViewMode === 'week' ? 'month' : 'week';
    setActiveViewMode(next);
    if (localStorageKey) {
      localStorage.setItem(localStorageKey, next);
    }
  };

  // State for month view navigation
  const [currentViewDate, setCurrentViewDate] = useState(() => {
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    return propSelectedDate || today;
  });

  // Update selected date when prop changes
  useEffect(() => {
    if (propSelectedDate) {
      // Intentional: local selectedDate is otherwise user-navigable (calendar clicks),
      // so it can't be computed purely from props during render — it must be re-synced
      // when the parent supplies a new externally-selected date.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedDate(propSelectedDate);
    }
  }, [propSelectedDate]);

  // Reset the displayed month when the parent supplies a new externally-selected date
  // (e.g. "Return to Today" or switching to a different child). Mirrors the selectedDate
  // sync above — currentViewDate is otherwise only driven by the prev/next month arrows,
  // so nothing else brings the displayed month back in sync with an external reset.
  // Note: unlike the arrow handlers, this does not call onDateRangeChange — safe today
  // because every current consumer independently re-fetches heart/progress data whenever
  // it resets selectedDate, but a future consumer relying on onDateRangeChange alone to
  // know the visible range would miss this transition.
  useEffect(() => {
    if (propSelectedDate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentViewDate(new Date(propSelectedDate.getFullYear(), propSelectedDate.getMonth(), 1));
    }
  }, [propSelectedDate]);

  // Calculate date range and days based on mode
  const { days } = React.useMemo(() => {
    if (activeViewMode === 'month') {
      // Full month view
      const year = currentViewDate.getFullYear();
      const month = currentViewDate.getMonth();

      // Start from first day of month
      const monthStart = new Date(year, month, 1);
      // End at last day of month
      const monthEnd = new Date(year, month + 1, 0);

      // Generate all days in the month, creating dates at noon to avoid timezone boundary issues
      const monthDays = [];
      for (let day = 1; day <= monthEnd.getDate(); day++) {
        // Create date at noon to avoid timezone boundary issues when clicking
        monthDays.push(new Date(year, month, day, 12, 0, 0));
      }

      return {
        days: monthDays,
        startDate: monthStart,
        endDate: monthEnd
      };
    } else {
      // Week view: 7 days of the current week (Sunday–Saturday containing today)
      const { startDate: weekStart, endDate: weekEnd } = getCurrentWeekDateRange(today);

      const weekDays = [];
      const cur = new Date(weekStart);
      while (cur <= weekEnd) {
        weekDays.push(new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), 12, 0, 0));
        cur.setDate(cur.getDate() + 1);
      }

      return {
        days: weekDays,
        startDate: weekStart,
        endDate: weekEnd
      };
    }
  }, [activeViewMode, currentViewDate, today]);

  // Use YYYY-MM-DD string keys so heart date lookups are timezone-safe.
  // new Date("2026-02-16") parses as UTC midnight and shifts to Feb 15 in negative-offset timezones.
  const toDateKey = (d: string) => d.split('T')[0];
  const heartDateSet = new Set(heartDates.map(toDateKey));
  const mendingHeartDateSet = new Set(mendingHeartDates.map(toDateKey));

  // Navigation functions for month mode
  const canNavigatePrevious = useCallback(() => {
    if (activeViewMode !== 'month') return true;
    if (!familyCreatedAt) return true; // Allow navigation if no family creation date

    const familyCreatedDate = new Date(familyCreatedAt);
    const prevMonth = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() - 1, 1);
    const familyCreatedMonth = new Date(familyCreatedDate.getFullYear(), familyCreatedDate.getMonth(), 1);

    return prevMonth >= familyCreatedMonth;
  }, [activeViewMode, familyCreatedAt, currentViewDate]);

  const canNavigateNext = useCallback(() => {
    if (activeViewMode !== 'month') return true;

    const nextMonth = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth() + 1, 1);
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    return nextMonth <= currentMonth;
  }, [activeViewMode, currentViewDate, today]);

  const navigateToPreviousMonth = useCallback(() => {
    if (!canNavigatePrevious()) return;

    setCurrentViewDate(prev => {
      const newDate = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);

      // Call the callback with the new month's date range
      if (onDateRangeChange && activeViewMode === 'month') {
        const monthStart = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        const monthEnd = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
        onDateRangeChange(monthStart, monthEnd);
      }

      return newDate;
    });
  }, [canNavigatePrevious, onDateRangeChange, activeViewMode]);

  const navigateToNextMonth = useCallback(() => {
    if (!canNavigateNext()) return;

    setCurrentViewDate(prev => {
      const newDate = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);

      // Call the callback with the new month's date range
      if (onDateRangeChange && activeViewMode === 'month') {
        const monthStart = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        const monthEnd = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
        onDateRangeChange(monthStart, monthEnd);
      }

      return newDate;
    });
  }, [canNavigateNext, onDateRangeChange, activeViewMode]);

  // Lookup map from date string (YYYY-MM-DD) to progress record for O(1) tooltip access
  const progressByDate = React.useMemo(() => {
    const map: Record<string, DailyProgress> = {};
    dailyProgress.forEach(dp => { map[dp.date] = dp; });
    return map;
  }, [dailyProgress]);

  // Popover state for day hover tooltip
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverDate, setPopoverDate] = useState<string | null>(null);

  const handleDayMouseEnter = (event: React.MouseEvent<HTMLElement>, date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (dateOnly >= todayOnly) return; // No tooltip for today or future
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setPopoverAnchor(event.currentTarget);
    setPopoverDate(dateStr);
  };

  const handleDayMouseLeave = () => {
    setPopoverAnchor(null);
    setPopoverDate(null);
  };

  // Day selection handler
  const handleDayClick = useCallback((date: Date) => {
    // Don't allow selection of future dates
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (dateOnly > todayOnly) {
      return; // Prevent selection of future dates
    }

    setSelectedDate(date);
    if (onDaySelect) {
      onDaySelect(date);
    }
  }, [onDaySelect, today]);

  if (loading) {
    const loadingContent = (
      <>
        {!hideChrome && (
          <Typography variant="h6" gutterBottom>
            Heart Calendar
          </Typography>
        )}
        <Typography>Loading...</Typography>
      </>
    );
    return hideChrome
      ? <Box>{loadingContent}</Box>
      : <Paper elevation={2} sx={{ p: 3, borderRadius: isNaluV1Theme ? '18px' : 2, ...(borderColor ? { border: `1px solid ${borderColor}` } : {}) }}>{loadingContent}</Paper>;
  }

  // Count all hearts (clean + mending) visible in current view
  const allHeartDates = [...heartDates, ...mendingHeartDates];
  const visibleHeartCount = activeViewMode === 'month'
    ? allHeartDates.filter(d => {
        const [y, m] = d.split('T')[0].split('-').map(Number);
        return y === currentViewDate.getFullYear() && m - 1 === currentViewDate.getMonth();
      }).length
    : allHeartDates.filter(d => {
        const [y, mo, day] = d.split('T')[0].split('-').map(Number);
        const date = new Date(y, mo - 1, day);
        const { startDate: ws, endDate: we } = getCurrentWeekDateRange(today);
        return date >= ws && date <= we;
      }).length;

  const Container: React.ElementType = hideChrome ? Box : Paper;
  // borderRadius: 2 is a bare sx number, which multiplies against theme.shape.borderRadius
  // (16 for nalu-v1) — 32px was too large next to the mockup's actual 18px card radius (issue #1900)
  const containerProps = hideChrome ? {} : {
    elevation: 2,
    sx: {
      p: 3,
      borderRadius: isNaluV1Theme ? '18px' : 2,
      ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
    },
  };

  return (
    <Container {...containerProps}>
      {/* Header */}
      {!hideChrome && (activeViewMode === 'month' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <IconButton
            onClick={navigateToPreviousMonth}
            disabled={!canNavigatePrevious()}
            aria-label="Previous month"
            size="small"
          >
            <ArrowBack />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {currentViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>

          <IconButton
            onClick={navigateToNextMonth}
            disabled={!canNavigateNext()}
            aria-label="Next month"
            size="small"
          >
            <ArrowForward />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Heart Calendar
          </Typography>
          {localStorageKey && (
            <IconButton
              onClick={handleToggleViewMode}
              aria-label="expand calendar"
              size="small"
            >
              <ExpandMore />
            </IconButton>
          )}
        </Box>
      ))}

      {/* Collapse button when in month view and toggle is available */}
      {!hideChrome && activeViewMode === 'month' && localStorageKey && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton
            onClick={handleToggleViewMode}
            aria-label="collapse calendar"
            size="small"
          >
            <ExpandLess />
          </IconButton>
        </Box>
      )}

      {/* Days of week header */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
        {DAYS_OF_WEEK.map((day, index) => (
          <Box
            key={`header-${index}`}
            textAlign="center"
            sx={{ fontWeight: 600, mb: 1 }}
            role="columnheader"
            aria-label={`${day === 'S' ? (index === 0 ? 'Sunday' : 'Saturday') :
                         day === 'M' ? 'Monday' :
                         day === 'T' ? (index === 2 ? 'Tuesday' : 'Thursday') :
                         day === 'W' ? 'Wednesday' : 'Friday'}`}
          >
            <Typography variant="body2" color="text.secondary">
              {day}
            </Typography>
          </Box>
        ))}

        {/* Fill empty cells at start of month for proper grid alignment (month view only) */}
        {activeViewMode === 'month' && days.length > 0 && Array.from({ length: days[0].getDay() }, (_, index) => (
          <Box key={`empty-${index}`} sx={{ height: CELL_HEIGHT }} />
        ))}

        {days.map((date) => {
          const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const hasHeart = heartDateSet.has(dateKey);
          const hasMendingHeart = mendingHeartDateSet.has(dateKey);
          const hasAnyHeart = hasHeart || hasMendingHeart;
          const isAway = progressByDate[dateKey]?.isAway ?? false;
          const isToday = date.toDateString() === today.toDateString();
          const isSelected = selectedDate.toDateString() === date.toDateString();
          // Compare calendar day, not raw instant: cells are noon-anchored while
          // `today` is midnight, so a same-day instant comparison would always
          // mark today's own cell as "future".
          const isPast = !isToday && date < today;
          const isFuture = !isToday && date > today;

          const shouldShowSelected = isSelected;

          // Pre-calculate styling values to avoid issues with sx prop
          const borderStyle = shouldShowSelected ? `2px solid ${theme.palette.secondary.main}` :
                             isToday ? `2px solid ${theme.palette.info.main}` :
                             `1px solid ${theme.palette.grey[300]}`;

          const backgroundStyle = shouldShowSelected ? alpha(theme.palette.secondary.main, 0.2) :
                                 isToday ? 'white' :
                                 isPast ? (hasAnyHeart ? alpha(theme.palette.behaviorHeart, 0.15) : theme.palette.grey[100]) :
                                 isFuture ? theme.palette.grey[50] :
                                 'white';

          const hoverBackgroundStyle = shouldShowSelected ? alpha(theme.palette.secondary.main, 0.3) :
                                      isToday ? theme.palette.grey[100] :
                                      theme.palette.grey[100];

          const dayOfMonth = date.getDate();
          const monthName = date.toLocaleDateString('en-US', { month: 'long' });

          const heartLabel = hasMendingHeart ? ', mending heart earned' : hasHeart ? ', heart earned' : '';
          const awayLabel = isAway ? ', away day' : '';

          // Allow button interaction for non-future dates when onDaySelect provided
          const cellComponent = onDaySelect && !isFuture ? 'button' : 'div';

          return (
            <Box
              key={`day-${date.getTime()}`}
              component={cellComponent}
              data-testid={`calendar-day-${date.getTime()}`}
              role={onDaySelect && !isFuture ? 'button' : 'gridcell'}
              aria-label={`${monthName} ${dayOfMonth}${isToday ? ', today' : ''}${heartLabel}${awayLabel}${isSelected ? ', selected' : ''}${isFuture ? ', future date, cannot select' : ''}`}
              onClick={onDaySelect && !isFuture ? () => handleDayClick(date) : undefined}
              onMouseEnter={!isToday && !isFuture ? (e: React.MouseEvent<HTMLElement>) => handleDayMouseEnter(e, date) : undefined}
              onMouseLeave={!isToday && !isFuture ? handleDayMouseLeave : undefined}
              tabIndex={onDaySelect && !isFuture ? 0 : undefined}
              sx={{
                height: CELL_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: borderStyle,
                borderRadius: isNaluV1Theme ? '8px' : 1,
                backgroundColor: backgroundStyle,
                opacity: isFuture ? 0.4 : 1,
                position: 'relative',
                cursor: onDaySelect && !isFuture ? 'pointer' :
                        isFuture ? 'not-allowed' : 'default',
                '&:hover': onDaySelect && !isFuture ? {
                  backgroundColor: hoverBackgroundStyle,
                } : {},
                '&:focus': {
                  outline: shouldShowSelected ? `2px solid ${theme.palette.secondary.main}` : `2px solid ${theme.palette.info.main}`,
                  outlineOffset: '-2px'
                },
                // Remove button styling
                ...(cellComponent === 'button' && {
                  background: backgroundStyle,
                  border: borderStyle,
                  padding: 0,
                  margin: 0,
                  fontFamily: 'inherit',
                })
              }}
            >
              {/* Day number in top-left corner */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 6,
                  fontSize: DAY_NUMBER_FONT_SIZE,
                  lineHeight: 1,
                  userSelect: 'none'
                }}
                aria-hidden="true"
              >
                {dayOfMonth}
              </Typography>

              {/* Clean heart icon — nudged down/off-center so it clears the day-number
                  corner above it rather than overlapping (issue #1900) */}
              {hasHeart && (
                <HeartIcon
                  data-testid="FavoriteOutlinedIcon"
                  sx={{
                    color: theme.palette.behaviorHeart,
                    fontSize: HEART_ICON_SIZE,
                    mt: '10px',
                  }}
                  aria-label="Heart earned"
                />
              )}

              {/* Mending heart emoji — heart earned after a square+MakeItRight cycle */}
              {hasMendingHeart && (
                <Box
                  component="span"
                  data-testid="mending-heart-icon"
                  aria-label="Mending heart earned"
                  sx={{ fontSize: HEART_ICON_SIZE, lineHeight: 1, mt: '10px' }}
                >
                  ❤️‍🩹
                </Box>
              )}

              {/* Travel icon in top-right corner for away days */}
              {isAway && (
                <AwayIcon
                  data-testid="away-day-icon"
                  aria-label="Away day"
                  sx={{
                    position: 'absolute',
                    top: 3,
                    right: 3,
                    fontSize: '10px',
                    color: theme.palette.info.main,
                    opacity: 0.8,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer summary */}
      {!hideChrome && (
        <Box mt={2} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            {activeViewMode === 'month'
              ? `${visibleHeartCount} hearts earned in ${currentViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
              : `${visibleHeartCount} hearts earned this week`
            }
          </Typography>
        </Box>
      )}

      {/* Day hover tooltip — shows behavior counts and no-heart reasons for past dates */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverDate(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
      >
        <DayTooltipCard
          dateStr={popoverDate}
          progress={popoverDate ? progressByDate[popoverDate] : undefined}
        />
      </Popover>
    </Container>
  );
};

export default HeartCalendar;
